import React from 'react';
import { useTimezone } from '../contexts/TimezoneContext';

/**
 * Lightweight inline markdown renderer.
 * Supports: **bold**, --- (horizontal rule), newlines, markdown tables.
 * Returns an array of React elements.
 */
const renderMarkdown = (text) => {
  if (!text) return text;

  // ---- Table detection ----
  // Split into segments: contiguous table rows vs non-table lines
  const lines = text.split('\n');
  const segments = []; // { type: 'text' | 'table', lines: [] }
  let current = null;

  lines.forEach((line) => {
    const isTableRow = /^\s*\|.*\|\s*$/.test(line);
    if (isTableRow) {
      if (!current || current.type !== 'table') {
        current = { type: 'table', lines: [] };
        segments.push(current);
      }
      current.lines.push(line);
    } else {
      if (!current || current.type !== 'text') {
        current = { type: 'text', lines: [] };
        segments.push(current);
      }
      current.lines.push(line);
    }
  });

  const elements = [];
  let segIdx = 0;

  segments.forEach((seg) => {
    if (seg.type === 'table') {
      // Parse markdown table
      const tableLines = seg.lines;
      if (tableLines.length < 2) {
        // Not enough rows for a table, render as text
        tableLines.forEach((line, li) => {
          elements.push(
            <React.Fragment key={`tl-${segIdx}-${li}`}>
              {renderInline(line)}
              {'\n'}
            </React.Fragment>
          );
        });
        segIdx++;
        return;
      }

      // Header row
      const headerCells = tableLines[0]
        .split('|')
        .filter((_, i, arr) => i > 0 && i < arr.length - 1)
        .map((c) => c.trim());

      // Determine where the separator row is (row of |---|---|)
      const sepIdx = tableLines.findIndex((l) => /^\s*\|[\s\-:|]+\|\s*$/.test(l));
      const dataStart = sepIdx >= 0 ? sepIdx + 1 : 1;

      const dataRows = tableLines.slice(dataStart).map((row) =>
        row
          .split('|')
          .filter((_, i, arr) => i > 0 && i < arr.length - 1)
          .map((c) => c.trim())
      );

      elements.push(
        <div key={`table-${segIdx}`} style={{ overflowX: 'auto', margin: '8px 0' }}>
          <table
            style={{
              borderCollapse: 'collapse',
              width: '100%',
              fontSize: '0.85em',
              lineHeight: '1.4',
            }}
          >
            <thead>
              <tr>
                {headerCells.map((cell, ci) => (
                  <th
                    key={ci}
                    style={{
                      borderBottom: '2px solid rgba(107, 114, 128, 0.3)',
                      padding: '6px 10px',
                      textAlign: 'left',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {renderInline(cell)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dataRows.map((row, ri) => (
                <tr key={ri}>
                  {row.map((cell, ci) => (
                    <td
                      key={ci}
                      style={{
                        borderBottom: '1px solid rgba(107, 114, 128, 0.15)',
                        padding: '5px 10px',
                        whiteSpace: ci === 0 ? 'nowrap' : 'normal',
                      }}
                    >
                      {renderInline(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      segIdx++;
    } else {
      // Normal text lines
      seg.lines.forEach((line, lineIdx) => {
        // Horizontal rule: line is only dashes (3+), optionally with whitespace
        if (/^\s*-{3,}\s*$/.test(line)) {
          elements.push(
            <hr
              key={`hr-${segIdx}-${lineIdx}`}
              className="my-2 border-t"
              style={{ borderColor: 'rgba(107, 114, 128, 0.3)' }}
            />
          );
          return;
        }

        elements.push(
          <React.Fragment key={`line-${segIdx}-${lineIdx}`}>
            {renderInline(line)}
            {'\n'}
          </React.Fragment>
        );
      });
      segIdx++;
    }
  });

  return elements;
};

/**
 * Render inline markdown: **bold** text.
 */
const renderInline = (text) => {
  if (!text) return text;
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    const boldMatch = part.match(/^\*\*(.+)\*\*$/);
    if (boldMatch) {
      return (
        <strong key={i} className="font-semibold">
          {boldMatch[1]}
        </strong>
      );
    }
    return part;
  });
};

const ChatMessage = ({ message, sender, timestamp, mode, onRetryAs }) => {
  const isAgent = sender === 'agent';
  const isUser = sender === 'user';
  const { formatTime } = useTimezone();
  const timeLabel = timestamp ? formatTime(timestamp) : '';

  // Determine if this is an ask-mode message
  const isAskMode = mode === 'ask';

  // Check if this is a long candidate listing message that needs scrolling.
  const messageLineCount = message ? message.split('\n').length : 0;
  const isCandidateListing = isAgent && message && (
    /Found \*\*\d+\*\* (result|candidate)/.test(message) ||
    /\*\*.*Pipeline\*\*/.test(message) ||
    /\|\s*#\s*\|/.test(message) ||
    /Here are the \*\*\d+\*\*/.test(message) ||
    /There are \*\*\d+\*\*/.test(message) ||
    /\*\*\d+\.\*\*/.test(message) ||
    (messageLineCount > 15)
  );

  // Pick the right CSS class based on sender + mode
  const messageClass = isUser
    ? 'chat_message_user'
    : isAskMode
    ? 'chat_message_ask'
    : 'chat_message_agent';

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} chat_mb_md`}>
      <div
        className={`chat_message_container ${messageClass}`}
      >
        {/* Mode badge — small inline label */}
        {isAgent && (
          <div className="flex items-center gap-2" style={{ marginBottom: '4px' }}>
            <span className={`chat-mode-badge ${isAskMode ? 'chat-mode-badge-ask' : 'chat-mode-badge-agent'}`}>
              {isAskMode ? 'Ask' : 'Agent'}
            </span>
          </div>
        )}

        <div
          className="chat_text_md whitespace-pre-wrap"
          style={{
            lineHeight: '1.4',
            overflowWrap: 'break-word',
            wordBreak: 'break-word',
            ...(isCandidateListing ? { maxHeight: '500px', overflowY: 'auto', paddingRight: '4px' } : {}),
          }}
        >
            {message && message.startsWith('Welcome to ') ? (
              (() => {
                const lines = message.split('\n');
                return (
                  <>
                    {lines.map((line, idx) => {
                      // First line — bold welcome title
                      if (idx === 0) {
                        return <span key={idx} className="font-semibold">{line}</span>;
                      }
                      // Lines with emoji + action title + dash + description
                      // e.g. "📝 Create a Job Description — Describe your ideal candidate"
                      const dashMatch = line.match(/^([\p{Emoji}\s]+)(.*?)\s*[—–-]\s*(.*)$/u);
                      if (dashMatch) {
                        return (
                          <React.Fragment key={idx}>
                            {'\n'}{dashMatch[1]}<span className="font-semibold">{dashMatch[2]}</span> — {dashMatch[3]}
                          </React.Fragment>
                        );
                      }
                      // Other lines — render as-is
                      return <React.Fragment key={idx}>{idx > 0 ? '\n' : ''}{line}</React.Fragment>;
                    })}
                  </>
                );
              })()
            ) : message && message.includes("Got it - here's what I understood:") ? (
              (() => {
                const lines = message.split('\n');
                return lines.map((line, index) => {
                  // Check if line matches pattern "Label: Value"
                  const colonIndex = line.indexOf(':');
                  if (colonIndex !== -1 && colonIndex < line.length - 1) {
                    const label = line.substring(0, colonIndex + 1);
                    const value = line.substring(colonIndex + 1).trim();
                    return (
                      <React.Fragment key={index}>
                        {label} <span className="font-bold">{value}</span>
                        {index < lines.length - 1 && '\n'}
                      </React.Fragment>
                    );
                  }
                  // Return line as-is if it doesn't match the pattern
                  return (
                    <React.Fragment key={index}>
                      {line}
                      {index < lines.length - 1 && '\n'}
                    </React.Fragment>
                  );
                });
              })()
            ) : (
              renderMarkdown(message)
            )}
        </div>
        <div
          className={`chat_text_xs mt-1 ${isUser ? 'text-right' : 'text-left'}`}
          style={{ color: 'rgba(107, 114, 128, 0.9)' }}
        >
          {timeLabel}
        </div>
      </div>
    </div>
  );
};

export default ChatMessage;
